import {AppRoute} from '../../models/AppRoute';
import {NestedRouteParams} from '../../navigation/offerRoute';
import {reset} from '../../navigation/RootNavigation';

// DIS-1730 (spec D3, A6): leave the rewards flow for a main screen. Resets the
// root navigator: the rewards flow is a ROOT sibling, so a screen-level
// navigation.reset would target the rewards stack, not the root.
// Nested `state` is valid for resetRoot but not in `reset`'s inferred default
// parameter type, so the state is built by a function (no excess-property
// check) instead of an inline literal.
const mainScreenState = (screen: AppRoute) => ({
  index: 0,
  routes: [
    {
      name: AppRoute.ROOT,
      state: {
        routes: [
          {
            name: AppRoute.MAIN,
            state: {
              routes: [{name: screen}],
            },
          },
        ],
      },
    },
  ],
});

// DIS-1826 (spec D5): reset to ROOT > MAIN > `screen`.
export function resetToMainScreen(screen: AppRoute, hasSelectedStore: boolean) {
  if (hasSelectedStore) {
    reset(mainScreenState(screen));
    return;
  }

  // No store selected — ROOT is not mounted.
  // Reset to store selection; after picking a store, ROOT mounts with HOME.
  reset({
    index: 0,
    routes: [{name: AppRoute.ROOT_STORE_SELECTION_FLOW}],
  });
}

export function resetToHome(hasSelectedStore: boolean) {
  resetToMainScreen(AppRoute.HOME, hasSelectedStore);
}

// DIS-1917 (spec § 4.3 R2): one route of a reset state. Each nested `screen`
// becomes a child navigator `state`, as mainScreenState writes it by hand;
// only the innermost route keeps `params`.
type ResetRoute = {
  name: AppRoute;
  params?: Record<string, unknown>;
  state?: {routes: ResetRoute[]};
};

const routeState = (name: AppRoute, params?: Record<string, unknown>): ResetRoute => {
  const nested = params as NestedRouteParams | undefined;
  if (nested?.screen) {
    return {name, state: {routes: [routeState(nested.screen, nested.params)]}};
  }
  return params ? {name, params} : {name};
};

// DIS-1917 (spec § 4.3 R2): reset the root navigator to the one route a
// `navigate(route, params)` tuple names. A navigate would move the existing
// ROOT route (both root screens set getId) above the rewards flow and leave the
// flow in the stack.
export function resetToRoute([name, params]: [AppRoute, NestedRouteParams]) {
  reset({index: 0, routes: [routeState(name, params)]});
}

type ActivateSuccessStores = {
  userStore: {takeOfferReturn: () => [AppRoute, NestedRouteParams] | null};
  shopSelectionStore: {selectedStore?: {id?: string} | null};
};

// DIS-1917: the end of a loyalty join (opt-in). A join started from an offer
// returns straight to that offer, so the member-card screen never renders.
// The store rule is the one Success's GO TO REWARDS uses: without a store the
// offer's store gate could not have been passed, so the intent is left to expire.
export function goToActivateSuccess(
  navigation: {
    navigate: (route: AppRoute.REWARDS_ACTIVATE_SUCCESS, params: {flow: 'activate'}) => void;
  },
  {userStore, shopSelectionStore}: ActivateSuccessStores,
) {
  const offerRoute = shopSelectionStore.selectedStore?.id ? userStore.takeOfferReturn() : null;
  if (offerRoute) {
    resetToRoute(offerRoute);
    return;
  }
  navigation.navigate(AppRoute.REWARDS_ACTIVATE_SUCCESS, {flow: 'activate'});
}

type SignInThenHomeStores = {
  userStore: {
    setPostLoginReturnToScreen: (screen: AppRoute | null, params?: NestedRouteParams) => void;
    takeOfferReturn: () => [AppRoute, NestedRouteParams] | null;
    setSignInAfterStoreSelection: (intent: {suburbSearch: string} | null) => void;
  };
};

// DIS-1859: a sign-up Success CTA sends the user to sign-in and lands them on
// Home afterwards, never on the Rewards card. With no store selected the
// store gate comes first: Find Your Store opens with `suburbSearch` prefilled,
// and Home continues to sign-in once a store is picked
// (HomepageScreen consumes `signInAfterStoreSelection`). The caller still
// navigates; this only records where the journey goes next. DIS-1917: a
// pending offer return replaces Home as the destination.
export function prepareSignInThenHome(
  {userStore}: SignInThenHomeStores,
  hasSelectedStore: boolean,
  suburbSearch?: string,
) {
  const [returnScreen, returnParams] = userStore.takeOfferReturn() ?? [
    AppRoute.ROOT,
    {screen: AppRoute.MAIN, params: {screen: AppRoute.HOME}},
  ];
  userStore.setPostLoginReturnToScreen(returnScreen, returnParams);
  userStore.setSignInAfterStoreSelection(
    hasSelectedStore ? null : {suburbSearch: suburbSearch ?? ''},
  );
}
